import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Modal,
  notification,
  Typography,
} from 'antd';

import Auth0RoleDetailsContext from '@totem/components/services/remoteAccess/auth0role/details/Auth0RoleDetailsContext';
import { RemoteAccessUserRolesAddRequest } from '@totem/components/services/remoteAccess/auth0role/details/types';
import StepResultsModal from '@totem/components/services/remoteAccess/auth0users/details/StepResultsModal';
import { MultiStepResults } from '@totem/components/services/remoteAccess/auth0users/details/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { AUTH0_ADD_USER_ROLES_ENDPOINT } from '@totem/utilities/endpoints';
import { validateEmail } from '@totem/utilities/validation';

const { Text } = Typography;

type Props = {
  visible: boolean;
  onClose: () => void;
};

const AddUserDialog = ({ visible, onClose }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { role, onRefresh } = useContext(Auth0RoleDetailsContext);

  const [step, setStep] = useState<'input' | 'confirm' | 'results'>('input');
  const [email, setEmail] = useState<string>('');
  const [givenName, setGivenName] = useState<string>('');
  const [familyName, setFamilyName] = useState<string>('');
  const [sendInvite, setSendInvite] = useState<boolean>(true);
  const [emailError, setEmailError] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<MultiStepResults | null>(null);

  const isInternal = permissions.internal.hasPermissions(userProfile);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setStep('input');

    setEmail('');

    setGivenName('');

    setFamilyName('');

    setSendInvite(true);

    setEmailError('');

    setLastResult(null);
  }, [visible]);

  const handleClose = () => {
    onClose();
  };

  const handleNext = () => {
    if (!email || !validateEmail(email)) {
      setEmailError('Please enter a valid email');

      return;
    }
    setEmailError('');

    setStep('confirm');
  };

  const handleSubmit = () => {
    if (!role || sending) {
      return;
    }
    setSending(true);

    const payload: RemoteAccessUserRolesAddRequest = {
      email,
      givenName,
      familyName,
      sendInvite,
      roles: [{ roleId: role.id, roleName: role.name }],
    };

    fetch(AUTH0_ADD_USER_ROLES_ENDPOINT, {
      method: 'PATCH',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result: MultiStepResults) => {
        onRefresh();

        if (isInternal) {
          setLastResult(result);

          setStep('results');

          return;
        }

        if (result?.success) {
          notification.success({
            message: 'User added',
            description: `Added ${email} to role ${role.name}.`,
          });

          onClose();
        } else {
          notification.error({
            message: 'Error adding user',
            description:
              result?.error ?? 'An error occurred while adding the user.',
            duration: 0,
          });
        }
      })
      .catch(() => {
        notification.error({
          message: 'Error adding user',
          description: 'An error occurred while adding the user.',
          duration: 0,
        });
      })
      .finally(() => setSending(false));
  };

  if (step === 'results') {
    return (
      <StepResultsModal
        visible={visible}
        title="Add User Result"
        result={lastResult}
        successMessage="User added"
        failureMessage="Add user failed"
        onClose={onClose}
      />
    );
  }

  if (step === 'confirm') {
    const fullName = [givenName, familyName].filter(Boolean).join(' ');

    return (
      <Modal
        open={visible}
        onCancel={handleClose}
        title={<ModalTitle>Confirm Add User</ModalTitle>}
        footer={
          <ModalFooter>
            <Button onClick={() => setStep('input')} disabled={sending}>
              Back
            </Button>
            <Button type="primary" onClick={handleSubmit} loading={sending}>
              Confirm
            </Button>
          </ModalFooter>
        }
      >
        <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Email">{email}</Descriptions.Item>
          {fullName && (
            <Descriptions.Item label="Name">{fullName}</Descriptions.Item>
          )}
          <Descriptions.Item label="Role">{role?.name}</Descriptions.Item>
        </Descriptions>
        <Text type="secondary">
          If {email} doesn&apos;t already have an account, one will be created
          {fullName ? ' using the name above' : ''}.
        </Text>
      </Modal>
    );
  }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      title={<ModalTitle>Add User to {role?.name}</ModalTitle>}
      footer={
        <ModalFooter>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" onClick={handleNext}>
            Next
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical">
        <Form.Item
          label="Email"
          required
          validateStatus={emailError ? 'error' : ''}
          help={emailError || undefined}
        >
          <Input
            value={email}
            onChange={(evt) => setEmail(evt.target.value)}
            placeholder="user@example.com"
          />
        </Form.Item>
        <Form.Item label="First Name" help="Used only if this is a new user">
          <Input
            value={givenName}
            onChange={(evt) => setGivenName(evt.target.value)}
          />
        </Form.Item>
        <Form.Item label="Last Name" help="Used only if this is a new user">
          <Input
            value={familyName}
            onChange={(evt) => setFamilyName(evt.target.value)}
          />
        </Form.Item>
        {isInternal && (
          <Form.Item>
            <Checkbox
              checked={sendInvite}
              onChange={(evt) => setSendInvite(evt.target.checked)}
            >
              Send remote access invite (applies if this is a top-level role)
            </Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default AddUserDialog;
